import { TrashIcon } from "@heroicons/react/outline";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { useReportContext } from "../../report-context";

import useColumnSaveHandlers from "./hooks/useColumnSaveHandlers";
import useColumnTypeHandler from "./hooks/useColumnTypeHandler";
import useSidebarState from "./hooks/useSidebarState";

import { ConfirmationModal, ConfirmationModalVariant } from "@/components/ConfirmationModal";
import PowerBlock from "@/components/PowerBlock";
import { BlockEnv } from "@/components/PowerBlock/Block";
import RetryButton from "@/components/PowerBlock/Block/RetryButton";
import { BlockType } from "@/components/PowerBlock/Blocks";
import { Button } from "@/components/ui/button";
import SidebarWithBlur from "@/components/ui/Sidebar/SidebarWithBlur";
import { useAuth } from "@/context/auth-context";
import { useRerunReportColumn } from "@/queries/evals";
import { useRbac } from "@/providers/RbacProvider";
import { EntityType } from "@/queries";

interface BlueprintSidebarProps {
  readonly?: boolean;
  type?: "create" | "update";
}

const MIN_SIDEBAR_WIDTH = 500;
const MAX_SIDEBAR_WIDTH = 800;

const BlueprintSidebar = observer((props: BlueprintSidebarProps) => {
  const { type } = props;
  const { isEditDisabled } = useRbac();
  const cannotEditBlueprint = isEditDisabled(EntityType.REPORT);
  const { selectedColumn, isOpen, isLoading, sources, allSources, handleCloseSidebar } =
    useSidebarState({ type });

  const { handleSave } = useColumnSaveHandlers({ type });
  const { handleTypeChange } = useColumnTypeHandler();
  const reportContext = useReportContext();
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);

  const auth = useAuth();
  const userToken = auth?.userToken ?? "";
  const { mutate: rerunColumn, isLoading: isRerunning } = useRerunReportColumn(
    userToken,
    reportContext.reportId,
  );

  const handleChange = (payload: {
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    configuration: any;
    allSources?: Record<string, string[]>;
  }) => {
    if (selectedColumn && !selectedColumn.id) {
      // This is a draft column, persist changes to context
      const updatedColumn = {
        ...selectedColumn,
        name: payload.name,
        configuration: payload.configuration,
      };

      // Update both selected column and draft column
      reportContext.setSelectedColumn(updatedColumn);
      reportContext.setPersistedDraftColumn(updatedColumn);
    }
  };

  const isDraftColumn = selectedColumn && !selectedColumn.id;

  const handleDiscardDraft = () => {
    setShowDiscardConfirmation(true);
  };

  const confirmDiscardDraft = () => {
    reportContext.clearPersistedDraftColumn();
    setShowDiscardConfirmation(false);
    reportContext.closeSidebar(true); // Skip draft persistence when discarding
  };

  const cancelDiscardDraft = () => {
    setShowDiscardConfirmation(false);
  };

  const handleRetry = () => {
    if (!selectedColumn?.id) {
      return;
    }

    rerunColumn({ reportColumnId: selectedColumn.id });
  };

  const shouldShowRetryButton =
    selectedColumn &&
    !isDraftColumn &&
    JSON.stringify(selectedColumn.configuration) !== "{}" &&
    !props.readonly;

  return (
    <SidebarWithBlur
      isOpen={!!isOpen}
      onClose={handleCloseSidebar}
      minWidth={MIN_SIDEBAR_WIDTH}
      maxWidth={MAX_SIDEBAR_WIDTH}
      defaultWidth={MIN_SIDEBAR_WIDTH}
      position="relative"
      resizable
      enableEscapeKey
      enableClickOutside={false}
      hasFooter={!props.readonly}
      className={`${
        isOpen ? " border-l border-gray-200" : "pointer-events-none flex-[0] opacity-0"
      }`}
    >
      {selectedColumn && (
        <div className="relative h-full">
          <PowerBlock
            banner={
              (isDraftColumn && (
                <div className="rounded-md border border-amber-200 bg-amber-50/50 px-3 py-2.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <span className="text-xs font-medium  tracking-wide text-amber-700">
                        Draft
                      </span>
                      <span className="text-xs text-amber-400">·</span>
                      <span className="text-xs text-amber-600">
                        Click "Save" to finalize this step
                      </span>
                    </div>
                    <Button
                      variant="destructiveLink"
                      size="sm"
                      onClick={handleDiscardDraft}
                      className="gap-x-1"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                      Discard
                    </Button>
                  </div>
                </div>
              )) ||
              undefined
            }
            isDraft={type === "create" || JSON.stringify(selectedColumn.configuration) === "{}"}
            key={`${selectedColumn.id || "new"}_${selectedColumn.column_type}`}
            env={BlockEnv.EVAL}
            id={selectedColumn.id}
            allSources={allSources}
            configuration={selectedColumn.configuration || {}}
            // onAutoSave={handleAutoSave}
            onSave={handleSave}
            onChange={handleChange}
            onClose={handleCloseSidebar}
            name={selectedColumn.name}
            readonly={props.readonly || cannotEditBlueprint}
            type={BlockType[selectedColumn.column_type as keyof typeof BlockType]}
            sources={sources}
            onTypeChange={!isLoading ? handleTypeChange : undefined}
            headerInjection={() =>
              shouldShowRetryButton && (
                <div className="flex w-full justify-end">
                  <RetryButton
                    onRetry={handleRetry}
                    isVisible
                    isLoading={isLoading || isRerunning}
                    tooltipContent="Retry executing this step"
                  />
                </div>
              )
            }
          />
        </div>
      )}
      <ConfirmationModal
        title="Discard Draft"
        description={`Are you sure you want to discard the draft "${selectedColumn?.name}"? This action cannot be undone.`}
        isOpen={showDiscardConfirmation}
        setIsOpen={setShowDiscardConfirmation}
        leaveButtonText="Cancel"
        saveButtonText="Discard"
        handleOnConfirm={confirmDiscardDraft}
        handleLeave={cancelDiscardDraft}
        handleClose={cancelDiscardDraft}
        headerVariant={ConfirmationModalVariant.warning}
        saveButtonVariant="destructive"
        exitButtonVariant="secondary"
      />
    </SidebarWithBlur>
  );
});

export default BlueprintSidebar;
